import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import "./AdminMatchManager.css";

import { db } from "../firebase";

import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";

export default function AdminMatchList() {
  const [matches, setMatches] = useState([]);
  const [search, setSearch] = useState("");

  // Firestore date values use YYYY-MM-DD.
  const getTodayDate = () => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Africa/Dar_es_Salaam",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());

    const values = Object.fromEntries(
      parts.map(({ type, value }) => [type, value])
    );

    return `${values.year}-${values.month}-${values.day}`;
  };

  const [selectedDate, setSelectedDate] = useState(getTodayDate);
  const [timeFilter, setTimeFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [correctScoresDraft, setCorrectScoresDraft] = useState([]);

  // =========================================================
  // FORM
  // =========================================================

  const [form, setForm] = useState({
    A: "",
    B: "",
    league: "",
    date: "",
    status: "",
    firstStatus: "",
    secondStatus: "",
    score: "",

    // FINAL RESULT ODDS
    odds: "",

    // MATCH ODDS
    homeOdds: "",
    drawOdds: "",
    awayOdds: "",
  });

  // =========================================================
  // EMPTY FORM
  // =========================================================

  const emptyForm = {
    A: "",
    B: "",
    league: "",
    date: "",
    status: "",
    firstStatus: "",
    secondStatus: "",
    score: "",
    odds: "",
    homeOdds: "",
    drawOdds: "",
    awayOdds: "",
  };

  // =========================================================
  // LOAD MATCHES
  // =========================================================

  const loadMatches = async () => {
    try {
      setLoading(true);
      const matchesQuery = query(
        collection(db, "matches"),
        where("date", "==", selectedDate)
      );
      const snap = await getDocs(matchesQuery);
      const data = snap.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      }));
      setMatches(data);
    } catch (error) {
      console.error("Error loading matches:", error);
      alert("Imeshindwa kupakia mechi. Tafadhali jaribu tena.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatches();
  }, [selectedDate]);

  // =========================================================
  // DELETE MATCH
  // =========================================================

  const deleteMatch = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this match?"
    );

    if (!confirmed) return;

    try {
      await deleteDoc(
        doc(db, "matches", id)
      );

      await loadMatches();
    } catch (error) {
      console.error(
        "Delete error:",
        error
      );

      alert(
        "Failed to delete match."
      );
    }
  };

  // =========================================================
  // OPEN EDIT MODAL
  // =========================================================

  const openEdit = (match) => {
    setEditing(match.id);
    setCorrectScoresDraft(
      Array.isArray(match.correctScores)
        ? match.correctScores.map((item) => ({ ...item }))
        : []
    );

    // -------------------------------------------------------
    // HANDLE ODDS MAP
    // -------------------------------------------------------

    const oddsObject =
      match.odds &&
      typeof match.odds === "object" &&
      !Array.isArray(match.odds)
        ? match.odds
        : {};

    // -------------------------------------------------------
    // HANDLE OLD NUMERIC ODDS
    //
    // If an old document has:
    //
    // odds: 4.85
    //
    // we put it into FINAL ODDS.
    // -------------------------------------------------------

    const finalOdds =
      typeof match.odds === "number"
        ? String(match.odds)
        : "";

    setForm({
      A: match.A || "",
      B: match.B || "",
      league: match.league || "",
      date: match.date || "",

      status: match.status || "",

      firstStatus:
        match.firstStatus || "",

      secondStatus:
        match.secondStatus || "",

      score: match.score || "",

      // FINAL RESULT ODDS
      odds: finalOdds,

      // MATCH ODDS MAP
      homeOdds:
        oddsObject.home !== undefined &&
        oddsObject.home !== null
          ? String(oddsObject.home)
          : "",

      drawOdds:
        oddsObject.draw !== undefined &&
        oddsObject.draw !== null
          ? String(oddsObject.draw)
          : "",

      awayOdds:
        oddsObject.away !== undefined &&
        oddsObject.away !== null
          ? String(oddsObject.away)
          : "",
    });
  };

  // =========================================================
  // CLOSE EDIT MODAL
  // =========================================================

  const closeEdit = () => {
    if (saving) return;

    setEditing(null);
    setForm(emptyForm);
    setCorrectScoresDraft([]);
  };

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (
    field,
    value
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // =========================================================
  // SAFE NUMBER
  // =========================================================

  const toNumberOrNull = (value) => {
    if (
      value === undefined ||
      value === null ||
      String(value).trim() === ""
    ) {
      return null;
    }

    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) {
      return null;
    }

    return numberValue;
  };

  // =========================================================
  // UPDATE MATCH
  // =========================================================

  const updateMatch = async () => {
    if (!editing) return;

    try {
      setSaving(true);

      // -----------------------------------------------------
      // FINAL ODDS
      //
      // Used by correct-score selection.
      // If empty, save null.
      // -----------------------------------------------------

      const finalOddsValue =
        form.odds === ""
          ? null
          : toNumberOrNull(form.odds);

      // -----------------------------------------------------
      // MATCH ODDS MAP
      // -----------------------------------------------------

      const homeOddsValue =
        toNumberOrNull(
          form.homeOdds
        );

      const drawOddsValue =
        toNumberOrNull(
          form.drawOdds
        );

      const awayOddsValue =
        toNumberOrNull(
          form.awayOdds
        );

      const updatedCorrectScores = correctScoresDraft.map((item) =>
        String(item?.score || "").trim() === form.score.trim()
          ? { ...item, odds: finalOddsValue }
          : { ...item }
      );

      await updateDoc(
        doc(
          db,
          "matches",
          editing
        ),
        {
          A: form.A.trim(),

          B: form.B.trim(),

          league:
            form.league.trim(),

          date:
            form.date.trim(),

          status:
            form.status.trim(),

          firstStatus:
            form.firstStatus.trim(),

          secondStatus:
            form.secondStatus.trim(),

          score:
            form.score.trim(),

          // FINAL CORRECT SCORE ODDS
          odds: finalOddsValue,
          correctScores: updatedCorrectScores,

          // -------------------------------------------------
          // MATCH 1X2 ODDS
          // -------------------------------------------------
          matchOdds: {
            home: homeOddsValue,
            draw: drawOddsValue,
            away: awayOddsValue,
          },
        }
      );

      setEditing(null);

      setForm(emptyForm);
      setCorrectScoresDraft([]);

      await loadMatches();
    } catch (error) {
      console.error(
        "Update error:",
        error
      );

      alert(
        "Failed to update match."
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // QUICK MAIN STATUS
  // =========================================================

  const updateStatus = async (
    match,
    status
  ) => {
    try {
      await updateDoc(
        doc(
          db,
          "matches",
          match.id
        ),
        {
          status,
        }
      );

      await loadMatches();
    } catch (error) {
      console.error(
        "Status update error:",
        error
      );
    }
  };

  // =========================================================
  // FIRST STATUS
  // =========================================================

  const updateFirstStatus =
    async (
      match,
      value
    ) => {
      try {
        await updateDoc(
          doc(
            db,
            "matches",
            match.id
          ),
          {
            firstStatus:
              value,
          }
        );

        await loadMatches();
      } catch (error) {
        console.error(
          "First status error:",
          error
        );
      }
    };

  // =========================================================
  // SECOND STATUS
  // =========================================================

  const updateSecondStatus =
    async (
      match,
      value
    ) => {
      try {
        await updateDoc(
          doc(
            db,
            "matches",
            match.id
          ),
          {
            secondStatus:
              value,
          }
        );

        await loadMatches();
      } catch (error) {
        console.error(
          "Second status error:",
          error
        );
      }
    };

  // =========================================================
  // APPLY CORRECT SCORE
  // =========================================================

  const applyCorrectScore = (
    correctScore
  ) => {
    if (!correctScore) return;

    setForm((prev) => ({
      ...prev,

      score:
        correctScore.score || "",

      odds:
        correctScore.odds !==
          undefined &&
        correctScore.odds !==
          null
          ? String(
              correctScore.odds
            )
          : "",
    }));
  };

  // =========================================================
  // FORMAT ODDS
  //
  // Handles:
  //
  // odds: {
  //   home: 4.1,
  //   draw: 4.2,
  //   away: 1.76
  // }
  //
  // AND:
  //
  // odds: 4.85
  // =========================================================

  const formatOdds = (odds) => {
    if (
      odds === undefined ||
      odds === null
    ) {
      return "—";
    }

    // NUMBER
    if (
      typeof odds === "number"
    ) {
      return String(odds);
    }

    // STRING
    if (
      typeof odds === "string"
    ) {
      return odds || "—";
    }

    // OBJECT / MAP
    if (
      typeof odds === "object" &&
      !Array.isArray(odds)
    ) {
      const home =
        odds.home ??
        odds.Home ??
        "—";

      const draw =
        odds.draw ??
        odds.Draw ??
        "—";

      const away =
        odds.away ??
        odds.Away ??
        "—";

      return  `${home}  |  D ${draw}  |  A ${away}`;
    }

    return "—";
  };

  // =========================================================
  // GET CURRENT MATCH
  // =========================================================

  const currentMatch = useMemo(() => {
    if (!editing) return null;

    return (
      matches.find(
        (match) =>
          match.id === editing
      ) || null
    );
  }, [
    editing,
    matches,
  ]);

  // =========================================================
  // GET CORRECT SCORES
  // =========================================================

  const correctScores = correctScoresDraft;

  // =========================================================
  // FILTER MATCHES
  // =========================================================

  const filtered = matches.filter((match) => {
    const queryText = search.trim().toLowerCase();

    const matchesSearch =
      !queryText ||
      [
        match.A,
        match.B,
        match.league,
        match.country,
        match.status,
        match.firstStatus,
        match.secondStatus,
        match.score,
        match.date,
        match.time,
        formatOdds(match.odds),
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(queryText)
        );

    const matchesTime =
      !timeFilter || String(match.time || "") === timeFilter;

    return matchesSearch && matchesTime;
  });

  // STATUS CLASS
  // =========================================================

  const getStatusClass = (
    status
  ) => {
    const value =
      String(
        status || ""
      ).toUpperCase();

    if (value === "OK") {
      return "status-ok";
    }

    if (value === "GO") {
      return "status-go";
    }

    if (value === "FIRST") {
      return "status-first";
    }

    if (value === "SECOND") {
      return "status-second";
    }

    if (
      value === "PENDING" ||
      value === "WAIT"
    ) {
      return "status-pending";
    }

    return "status-empty";
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="admin-match-manager">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="amm-header">

        <div>

          <div className="amm-system-label">
            DIGITAL ADMIN
          </div>

          <h1>
            Match Center
          </h1>

          <p>
            Manage matches, signals,
            scores and odds.
          </p>

        </div>

        <div className="amm-live">

          <span />

          LIVE SYSTEM

        </div>

      </div>

      {/* =====================================================
          SEARCH / COUNTER
      ===================================================== */}

      <div className="amm-controls">

        <div className="amm-search">

          <span className="amm-search-icon">
            ⌕
          </span>

          <input
            type="text"
            placeholder="Search team, league, status, score..."
            value={search}
            onChange={(e) =>
              setSearch(
                e.target.value
              )
            }
          />

        </div>

        <div className="amm-date-filter">
          <label htmlFor="amm-match-date">DATE</label>
          <input
            id="amm-match-date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
          <button
            type="button"
            onClick={() => {
              setSelectedDate(getTodayDate());
              setTimeFilter("");
              setSearch("");
            }}
          >
            TODAY
          </button>
          <label htmlFor="amm-match-time">TIME</label>
          <input
            id="amm-match-time"
            type="time"
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
          />
        </div>

        <div className="amm-counter">

          <span>
            MATCHES
          </span>

          <strong>
            {filtered.length}
          </strong>

          <small>
            / {matches.length}
          </small>

        </div>

      </div>

      {/* =====================================================
          MATCH LIST
      ===================================================== */}

      {loading ? (
        <div className="amm-empty">
          <h3>Loading matches...</h3>
          <p>Please wait.</p>
        </div>
      ) : filtered.length === 0 ? (

        <div className="amm-empty">

          <div>
            ⌕
          </div>

          <h3>
            No matches found
          </h3>

          <p>
            Try another search.
          </p>

        </div>

      ) : (

        filtered.map(
          (match) => (

            <div
              key={match.id}
              className="amm-match-card"
            >

              {/* =================================================
                  CARD HEADER
              ================================================= */}

              <div className="amm-card-top">

                <div className="amm-match-id">

                  MATCH ID

                  <span>
                    {match.id}
                  </span>

                </div>

                <div
                  className={`amm-main-status ${getStatusClass(
                    match.status
                  )}`}
                >
                  {match.status ||
                    "NO STATUS"}
                </div>

              </div>

              {/* =================================================
                  TEAMS
              ================================================= */}

              <div className="amm-teams">

                <div className="amm-team amm-team-a">

                  {match.logoA ? (

                    <img
                      src={match.logoA}
                      alt={
                        match.A ||
                        "Team A"
                      }
                    />

                  ) : (

                    <div className="amm-logo-placeholder">
                      A
                    </div>

                  )}

                  <strong>
                    {match.A ||
                      "Team A"}
                  </strong>

                </div>

                <div className="amm-versus">

                  <span>
                    VS
                  </span>

                  <small>
                    {match.time ||
                      "--:--"}
                  </small>

                </div>

                <div className="amm-team amm-team-b">

                  <strong>
                    {match.B ||
                      "Team B"}
                  </strong>

                  {match.logoB ? (

                    <img
                      src={match.logoB}
                      alt={
                        match.B ||
                        "Team B"
                      }
                    />

                  ) : (

                    <div className="amm-logo-placeholder">
                      B
                    </div>

                  )}

                </div>

              </div>

              {/* =================================================
                  LEAGUE
              ================================================= */}

              <div className="amm-league">

                <span>
                  ◈
                </span>

                <strong>
                  {match.league ||
                    "Unknown League"}
                </strong>

                <i>
                  {match.country ||
                    "—"}
                </i>

              </div>

              {/* =================================================
                  SIGNAL STATUS
              ================================================= */}

              <div className="amm-section">

                <div className="amm-section-title">

                  <span>
                    SIGNAL STATUS
                  </span>

                  <em>
                    LIVE CONTROL
                  </em>

                </div>

                <div className="amm-signals">

                  {/* MAIN */}

                  <div className="amm-signal">

                    <div className="amm-signal-icon main">
                      ✓
                    </div>

                    <div>

                      <span>
                        MAIN
                      </span>

                      <strong
                        className={getStatusClass(
                          match.status
                        )}
                      >
                        {match.status ||
                          "—"}
                      </strong>

                    </div>

                  </div>

                  {/* FIRST */}

                  <div className="amm-signal">

                    <div className="amm-signal-icon first">
                      1
                    </div>

                    <div>

                      <span>
                        FIRST
                      </span>

                      <strong
                        className={getStatusClass(
                          match.firstStatus
                        )}
                      >
                        {match.firstStatus ||
                          "—"}
                      </strong>

                    </div>

                  </div>

                  {/* SECOND */}

                  <div className="amm-signal">

                    <div className="amm-signal-icon second">
                      2
                    </div>

                    <div>

                      <span>
                        SECOND
                      </span>

                      <strong
                        className={getStatusClass(
                          match.secondStatus
                        )}
                      >
                        {match.secondStatus ||
                          "—"}
                      </strong>

                    </div>

                  </div>

                </div>

              </div>

              {/* =================================================
                  CORRECT SCORES
              ================================================= */}

              <div className="amm-section">

                <div className="amm-section-title">

                  <span>
                    CORRECT SCORE & ODDS
                  </span>

                  <em>
                    {Array.isArray(
                      match.correctScores
                    )
                      ? `${match.correctScores.length} OPTIONS`
                      : "0 OPTIONS"}
                  </em>

                </div>

                {Array.isArray(
                  match.correctScores
                ) &&
                match.correctScores.length >
                  0 ? (

                  <div className="amm-score-grid">

                    {match.correctScores.map(
                      (
                        item,
                        index
                      ) => (

                        <div
                          className="amm-score-card"
                          key={`${match.id}-${index}`}
                        >

                          <span>
                            #{index + 1}
                          </span>

                          <strong>
                            {item?.score ||
                              "—"}
                          </strong>

                          <small>
                            ODDS{" "}
                            {item?.odds ??
                              "—"}
                          </small>

                        </div>

                      )
                    )}

                  </div>

                ) : (

                  <div className="amm-no-score">
                    No correct scores available.
                  </div>

                )}

              </div>

              {/* =================================================
                  RESULT
              ================================================= */}

              <div className="amm-result">

                {/* RESULT SCORE */}

                <div>

                  <span>
                    RESULT SCORE
                  </span>

                  <strong>
                    {match.score ||
                      "—"}
                  </strong>

                </div>

                {/* RESULT ODDS */}

                <div>

                  <span>
                    RESULT ODDS
                  </span>

                  <strong
                    className="amm-odds-display"
                  >
                    {formatOdds(
                      match.odds
                    )}
                  </strong>

                </div>

                {/* TIME */}

                <div>

                  <span>
                    TIME
                  </span>

                  <strong>
                    {match.time ||
                      "—"}
                  </strong>

                </div>

              </div>

              {/* =================================================
                  MATCH INFORMATION
              ================================================= */}

              <div className="amm-section">

                <div className="amm-section-title">

                  <span>
                    MATCH INFORMATION
                  </span>

                </div>

                <div className="amm-info-grid">

                  <div>

                    <span>
                      DATE
                    </span>

                    <strong>
                      {match.date ||
                        "—"}
                    </strong>

                  </div>

                  <div>

                    <span>
                      COUNTRY
                    </span>

                    <strong>
                      {match.country ||
                        "—"}
                    </strong>

                  </div>

                  <div>

                    <span>
                      SOURCE
                    </span>

                    <strong>
                      {match.source ||
                        "—"}
                    </strong>

                  </div>

                  <div>

                    <span>
                      TIMEZONE
                    </span>

                    <strong>
                      {match.timezone ||
                        "—"}
                    </strong>

                  </div>

                  <div>

                    <span>
                      SOFA ID
                    </span>

                    <strong>
                      {match.sofaId ||
                        "—"}
                    </strong>

                  </div>

                  <div>

                    <span>
                      START TIMESTAMP
                    </span>

                    <strong>
                      {match.startTimestamp
                        ? String(
                            match.startTimestamp
                          )
                        : "—"}
                    </strong>

                  </div>

                </div>

              </div>

              {/* =================================================
                  ACTIONS
              ================================================= */}

              <div className="amm-actions-panel">

                <div className="amm-action-title">
                  CONTROL ACTIONS
                </div>

                <div className="amm-actions">

                  {/* EDIT */}

                  <button
                    type="button"
                    className="amm-btn edit"
                    onClick={() =>
                      openEdit(match)
                    }
                  >
                    ✏️ EDIT
                  </button>

                  {/* DELETE */}

                  <button
                    type="button"
                    className="amm-btn delete"
                    onClick={() =>
                      deleteMatch(
                        match.id
                      )
                    }
                  >
                    🗑️ DELETE
                  </button>

                  {/* OK */}

                  <button
                    type="button"
                    className="amm-btn ok"
                    onClick={() =>
                      updateStatus(
                        match,
                        "OK"
                      )
                    }
                  >
                    ✓ OK
                  </button>

                  {/* GO */}

                  <button
                    type="button"
                    className="amm-btn go"
                    onClick={() =>
                      updateStatus(
                        match,
                        "GO"
                      )
                    }
                  >
                    🚀 GO
                  </button>

                  {/* FIRST */}

                  <button
                    type="button"
                    className="amm-btn first"
                    onClick={() =>
                      updateFirstStatus(
                        match,
                        "FIRST"
                      )
                    }
                  >
                    🥇 FIRST
                  </button>

                  {/* FIRST PENDING */}

                  <button
                    type="button"
                    className="amm-btn pending"
                    onClick={() =>
                      updateFirstStatus(
                        match,
                        "PENDING"
                      )
                    }
                  >
                    ⏳ FIRST PENDING
                  </button>

                  {/* SECOND */}

                  <button
                    type="button"
                    className="amm-btn second"
                    onClick={() =>
                      updateSecondStatus(
                        match,
                        "SECOND"
                      )
                    }
                  >
                    🥈 SECOND
                  </button>

                  {/* SECOND WAIT */}

                  <button
                    type="button"
                    className="amm-btn wait"
                    onClick={() =>
                      updateSecondStatus(
                        match,
                        "WAIT"
                      )
                    }
                  >
                    ⌛ SECOND WAIT
                  </button>

                </div>

              </div>

            </div>

          )
        )

      )}

      {/* =======================================================
          EDIT MODAL
      ======================================================= */}

      {editing &&
        currentMatch && (

          <div
            className="amm-modal"
            onMouseDown={(e) => {

              if (
                e.target ===
                e.currentTarget
              ) {
                closeEdit();
              }

            }}
          >

            <div className="amm-modal-box">

              {/* =================================================
                  MODAL HEADER
              ================================================= */}

              <div className="amm-modal-header">

                <div>

                  <span>
                    EDIT MATCH
                  </span>

                  <h2>

                    {currentMatch.A ||
                      "Team A"}

                    {" "}

                    vs

                    {" "}

                    {currentMatch.B ||
                      "Team B"}

                  </h2>

                  <p>
                    {currentMatch.league ||
                      "Unknown League"}
                  </p>

                </div>

                <button
                  type="button"
                  className="amm-close"
                  onClick={
                    closeEdit
                  }
                  disabled={saving}
                >
                  ×
                </button>

              </div>

              {/* =================================================
                  FORM
              ================================================= */}

              <div className="amm-form">

                {/* TEAM A */}

                <label>

                  <span>
                    TEAM A
                  </span>

                  <input
                    type="text"
                    value={form.A}
                    onChange={(e) =>
                      handleChange(
                        "A",
                        e.target.value
                      )
                    }
                  />

                </label>

                {/* TEAM B */}

                <label>

                  <span>
                    TEAM B
                  </span>

                  <input
                    type="text"
                    value={form.B}
                    onChange={(e) =>
                      handleChange(
                        "B",
                        e.target.value
                      )
                    }
                  />

                </label>

                {/* LEAGUE */}

                <label>

                  <span>
                    LEAGUE
                  </span>

                  <input
                    type="text"
                    value={
                      form.league
                    }
                    onChange={(e) =>
                      handleChange(
                        "league",
                        e.target.value
                      )
                    }
                  />

                </label>

                {/* DATE */}

                <label>

                  <span>
                    DATE
                  </span>

                  <input
                    type="text"
                    value={
                      form.date
                    }
                    onChange={(e) =>
                      handleChange(
                        "date",
                        e.target.value
                      )
                    }
                  />

                </label>

                {/* MAIN STATUS */}

                <label>

                  <span>
                    MAIN STATUS
                  </span>

                  <input
                    type="text"
                    value={
                      form.status
                    }
                    onChange={(e) =>
                      handleChange(
                        "status",
                        e.target.value
                      )
                    }
                    placeholder="OK / GO"
                  />

                </label>

                {/* FIRST STATUS */}

                <label>

                  <span>
                    FIRST STATUS
                  </span>

                  <input
                    type="text"
                    value={
                      form.firstStatus
                    }
                    onChange={(e) =>
                      handleChange(
                        "firstStatus",
                        e.target.value
                      )
                    }
                    placeholder="FIRST / PENDING"
                  />

                </label>

                {/* SECOND STATUS */}

                <label>

                  <span>
                    SECOND STATUS
                  </span>

                  <input
                    type="text"
                    value={
                      form.secondStatus
                    }
                    onChange={(e) =>
                      handleChange(
                        "secondStatus",
                        e.target.value
                      )
                    }
                    placeholder="SECOND / WAIT"
                  />

                </label>

                {/* SCORE */}

                <label>

                  <span>
                    FINAL SCORE
                  </span>

                  <input
                    type="text"
                    value={
                      form.score
                    }
                    onChange={(e) =>
                      handleChange(
                        "score",
                        e.target.value
                      )
                    }
                    placeholder="Example: 2-1"
                  />

                </label>

                {/* =================================================
                    FINAL ODDS
                ================================================= */}

                <label>

                  <span>
                    FINAL CORRECT SCORE ODDS
                  </span>

                  <input
                    type="number"
                    step="0.01"
                    value={
                      form.odds
                    }
                    onChange={(e) =>
                      handleChange(
                        "odds",
                        e.target.value
                      )
                    }
                    placeholder="Example: 4.85"
                  />

                </label>

                {/* =================================================
                    HOME ODDS
                ================================================= */}

                <label>

                  <span>
                    HOME ODDS
                  </span>

                  <input
                    type="number"
                    step="0.01"
                    value={
                      form.homeOdds
                    }
                    onChange={(e) =>
                      handleChange(
                        "homeOdds",
                        e.target.value
                      )
                    }
                    placeholder="Example: 4.10"
                  />

                </label>

                {/* =================================================
                    DRAW ODDS
                ================================================= */}

                <label>

                  <span>
                    DRAW ODDS
                  </span>

                  <input
                    type="number"
                    step="0.01"
                    value={
                      form.drawOdds
                    }
                    onChange={(e) =>
                      handleChange(
                        "drawOdds",
                        e.target.value
                      )
                    }
                    placeholder="Example: 4.20"
                  />

                </label>

                {/* =================================================
                    AWAY ODDS
                ================================================= */}

                <label>

                  <span>
                    AWAY ODDS
                  </span>

                  <input
                    type="number"
                    step="0.01"
                    value={
                      form.awayOdds
                    }
                    onChange={(e) =>
                      handleChange(
                        "awayOdds",
                        e.target.value
                      )
                    }
                    placeholder="Example: 1.76"
                  />

                </label>

              </div>

              {/* =================================================
                  CORRECT SCORES
              ================================================= */}

              <div className="amm-edit-scores">

                <div className="amm-edit-scores-header">

                  <div>

                    <span>
                      AVAILABLE CORRECT SCORES
                    </span>

                    <h3>
                      Chagua Score & Odds
                    </h3>

                    <p>
                      Bonyeza option yoyote
                      hapa chini kuweka
                      Score na Odds
                      automatically.
                    </p>

                  </div>

                  <div className="amm-options-count">

                    {correctScores.length}

                    <small>
                      OPTIONS
                    </small>

                  </div>

                </div>

                {correctScores.length >
                0 ? (

                  <div className="amm-edit-score-grid">
                    {correctScores.map((item, index) => {
                      const itemScore = String(item?.score ?? "");
                      const itemOdds = String(item?.odds ?? "");
                      const isSelected =
                        String(form.score || "").trim() === itemScore.trim() &&
                        String(form.odds || "").trim() === itemOdds.trim();

                      const updateScoreOption = (field, value) => {
                        setCorrectScoresDraft((prev) =>
                          prev.map((scoreItem, scoreIndex) =>
                            scoreIndex === index
                              ? {
                                  ...scoreItem,
                                  [field]:
                                    field === "odds" && value !== ""
                                      ? Number(value)
                                      : value,
                                }
                              : scoreItem
                          )
                        );

                        if (
                          field === "score" &&
                          String(form.score || "").trim() === itemScore.trim()
                        ) {
                          handleChange("score", value);
                        }
                        if (
                          field === "odds" &&
                          String(form.score || "").trim() === itemScore.trim()
                        ) {
                          handleChange("odds", value);
                        }
                      };

                      return (
                        <div
                          className={`amm-edit-score-card ${isSelected ? "selected" : ""}`}
                          key={`edit-score-${index}`}
                          style={{ cursor: "default" }}
                        >
                          <div className="amm-score-index">#{index + 1}</div>

                          <label style={{ display: "block", margin: "8px 0" }}>
                            <span style={{ display: "block", fontSize: "11px", marginBottom: "4px" }}>
                              SCORE (EDITABLE)
                            </span>
                            <input
                              type="text"
                              value={itemScore}
                              placeholder="Example: 2-1"
                              onChange={(e) => updateScoreOption("score", e.target.value)}
                              style={{ width: "100%", boxSizing: "border-box" }}
                            />
                          </label>

                          <label style={{ display: "block", margin: "8px 0" }}>
                            <span style={{ display: "block", fontSize: "11px", marginBottom: "4px" }}>
                              ODDS (EDITABLE)
                            </span>
                            <input
                              type="number"
                              min="1"
                              step="0.01"
                              value={itemOdds}
                              placeholder="Example: 4.85"
                              onChange={(e) => updateScoreOption("odds", e.target.value)}
                              style={{ width: "100%", boxSizing: "border-box" }}
                            />
                          </label>

                          <button
                            type="button"
                            className="amm-use-score"
                            onClick={() => applyCorrectScore(item)}
                          >
                            {isSelected ? "✓ SELECTED" : "USE THIS SCORE"}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                ) : (

                  <div className="amm-no-correct-scores">

                    <strong>
                      No Correct Scores
                    </strong>

                    <span>
                      This match has no
                      correctScores array.
                    </span>

                  </div>

                )}

              </div>

              {/* =================================================
                  CURRENT MATCH ODDS PREVIEW
              ================================================= */}

              <div className="amm-selected-preview">

                <div>

                  <span>
                    SELECTED SCORE
                  </span>

                  <strong>
                    {form.score ||
                      "—"}
                  </strong>

                </div>

                <div>

                  <span>
                    SELECTED ODDS
                  </span>

                  <strong>
                    {form.odds ||
                      "—"}
                  </strong>

                </div>

              </div>

              {/* =================================================
                  1X2 ODDS PREVIEW
              ================================================= */}

              <div className="amm-selected-preview">

                <div>

                  <span>
                    HOME
                  </span>

                  <strong>
                    {form.homeOdds ||
                      "—"}
                  </strong>

                </div>

                <div>

                  <span>
                    DRAW
                  </span>

                  <strong>
                    {form.drawOdds ||
                      "—"}
                  </strong>

                </div>

                <div>

                  <span>
                    AWAY
                  </span>

                  <strong>
                    {form.awayOdds ||
                      "—"}
                  </strong>

                </div>

              </div>

              {/* =================================================
                  MODAL ACTIONS
              ================================================= */}

              <div className="amm-modal-actions">

                <button
                  type="button"
                  className="amm-cancel"
                  onClick={
                    closeEdit
                  }
                  disabled={saving}
                >
                  CANCEL
                </button>

                <button
                  type="button"
                  className="amm-save"
                  onClick={
                    updateMatch
                  }
                  disabled={saving}
                >
                  {saving
                    ? "SAVING..."
                    : "💾 SAVE CHANGES"}
                </button>

              </div>

            </div>

          </div>

        )}

    </div>
  );
}